import { memo } from 'react';
import { Bot } from 'lucide-react';
import { cn } from '@/lib/utils';

interface IconConfig {
  slug: string;
  /** If true, icon will be inverted in dark mode (for monochrome black icons) */
  invertInDark?: boolean;
}

/**
 * Map of provider IDs to their icon config in @lobehub/icons-static-svg.
 * See: https://github.com/lobehub/lobe-icons
 */
const PROVIDER_ICONS: Record<string, IconConfig> = {
  anthropic: { slug: 'claude-color' },
  openai: { slug: 'openai', invertInDark: true },
  deepseek: { slug: 'deepseek-color' },
  google: { slug: 'google-color' },
  meta: { slug: 'meta-color' },
  mistral: { slug: 'mistral-color' },
  cohere: { slug: 'cohere-color' },
  perplexity: { slug: 'perplexity-color' },
  nvidia: { slug: 'nvidia', invertInDark: true },
  amazon: { slug: 'aws', invertInDark: true },
  ai21: { slug: 'ai21', invertInDark: true },
  huggingface: { slug: 'huggingface-color' },
  groq: { slug: 'groq', invertInDark: true },
  fireworks: { slug: 'fireworks-color' },
  replicate: { slug: 'replicate', invertInDark: true },
  baichuan: { slug: 'baichuan-color' },
  zhipu: { slug: 'zhipu-color' },
  qwen: { slug: 'qwen-color' },
  yi: { slug: 'yi-color' },
  minimax: { slug: 'minimax-color' },
  moonshot: { slug: 'moonshot', invertInDark: true },
  databricks: { slug: 'databricks-color' },
  microsoft: { slug: 'azure-color' },
  openrouter: { slug: 'openrouter', invertInDark: true },
  together: { slug: 'together-ai-color' },
  xai: { slug: 'grok', invertInDark: true },
};

const ICON_BASE_URL = 'https://unpkg.com/@lobehub/icons-static-svg@latest/icons';

interface ProviderIconProps {
  provider: string;
  className?: string;
  size?: number;
}

/**
 * Displays the icon for an AI model provider.
 * Falls back to a generic Bot icon if the provider is not recognized.
 */
export const ProviderIcon = memo(function ProviderIcon({
  provider,
  className,
  size = 16,
}: ProviderIconProps) {
  const normalizedProvider = provider.toLowerCase();
  const iconConfig = PROVIDER_ICONS[normalizedProvider];

  if (iconConfig) {
    return (
      <img
        src={`${ICON_BASE_URL}/${iconConfig.slug}.svg`}
        alt={`${provider} icon`}
        width={size}
        height={size}
        className={cn(
          'flex-shrink-0',
          iconConfig.invertInDark && 'dark:invert',
          className
        )}
        loading="lazy"
      />
    );
  }

  // Fallback to generic bot icon
  return (
    <Bot
      className={cn('flex-shrink-0 text-muted-foreground', className)}
      size={size}
    />
  );
});
